/* eslint-disable max-lines */
import React, {useCallback, useMemo} from "react";
import {
  AvatarAction,
  ChoiceUserInputAction,
  ChoiceUserInputBranchInput,
  ChoiceUserInputBranchState,
  Challenge,
  Scalars,
  Scenario,
  SystemAction,
  ChallengeMode,
} from "../../schema";
import {useTour} from "../../providers/tour";
import {isEditorChallengeEditTourMock} from "../../scenes/editor/challenge-edit.tour";
import Timeline from "./Timeline";
import ActionEditForm from "./actions-forms/ActionEditForm";
import {analyticsSendEvent} from "../../libs/analytics";
import {gql} from "@apollo/client";
import {useAudio} from "../../providers/audio";
import {useTranslation} from "react-i18next";

import AvatarIcon from "../AvatarIcon";
import AvatarBubble from "./actions/AvatarBubble";
import SystemIcon from "../SystemIcon";
import SystemBubble from "./actions/SystemBubble";
import UserIcon from "./UserIcon";
import UserInputBubble from "./actions/UserInputBubble";
import ChoiceUserInputBubble from "./actions/ChoiceUserInputBubble";
import ChallengeActionsAddRow from "./ChallengeActionsAddRow";
import Button from "../../ui/button";
import {DeleteIcon, CopyIcon} from "../../ui/icons";
import {Action, ActionInput} from "../../types";
import PlaceholderIcon from "./PlaceholderIcon";
import Bubble from "./Bubble";

type Props = {
  challenge: Challenge;
  action: Action;
  scenarios?: Scenario[];
  disabled?: boolean;

  onAdd?: (actionType: Action["__typename"], data: ActionInput) => void;
  onUpdate?: (actionId: string, actionType: Action["__typename"], data: ActionInput) => void;
  onDelete?: (actionId: string) => void;
  onCopy?: (actionId: string) => void;
  editActionModal: (action: Action, branchId?: string | undefined) => void;
  editQuestionModal: (action: ChoiceUserInputAction) => void;
};

export default function ChallengeAction({
  challenge,
  action,
  scenarios,
  disabled,
  onAdd,
  onUpdate,
  onDelete,
  onCopy,
  editActionModal,
  editQuestionModal,
}: Props) {
  const challengeID = challenge.id;

  const {mock: tourMock} = useTour();
  const getMockByActionID = useMemo(() => {
    if (tourMock && isEditorChallengeEditTourMock(tourMock)) {
      return tourMock.mockBy.action.get.bind(tourMock.mockBy.action);
    }
  }, [tourMock]);

  const insertHandler = useCallback(
    (actionType: Action["__typename"]) => {
      onAdd &&
        onAdd(actionType, {
          beforeActionId: action.id,
        });
    },
    [action, onAdd],
  );

  const {audioManager} = useAudio();

  const handlers = useMemo(() => {
    switch (action.__typename) {
      case "AvatarAction":
      case "SystemAction":
        return {
          onClick: () => editActionModal(action),

          onPlayClick: (e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();

            if (!action.audio) {
              console.warn("Cannot play action preview: no mediafile");
              return;
            }

            audioManager.getPlayback("editor")!.play(action.audio).catch(console.error);
            analyticsSendEvent("editorChallengeSoundPreviewAction", {
              challengeId: challengeID,
              actionId: action.id,
            });
          },
          onDelete: () => onDelete && onDelete(action.id),
          onCopy: () => onCopy && onCopy(action.id),
        };

      case "UserInputAction":
        return {
          onClick: () => editActionModal(action),
          onDelete: () => onDelete && onDelete(action.id),
          onCopy: () => onCopy && onCopy(action.id),
        };

      case "ChoiceUserInputAction":
        return {
          onMultipleChoiceToggle: () => {
            onUpdate && onUpdate(action.id, action.__typename, {isMultipleChoice: !action.isMultipleChoice});
          },

          onAddBranchClick: () => {
            onUpdate &&
              onUpdate(action.id, action.__typename, {
                branches: [
                  ...action.branches.map((branch) => ({
                    id: branch.id,
                  })),
                  {
                    state: ChoiceUserInputBranchState.CORRECT,
                  },
                ],
              });
          },

          onBranchClick: (branchId: Scalars["ID"]) => {
            editActionModal(action, branchId);
          },

          onQuestionClick: () => {
            editQuestionModal(action);
          },

          onDeleteBranchClick: (branchId: Scalars["ID"]) => {
            onUpdate &&
              onUpdate(action.id, action.__typename, {
                branches: action.branches
                  .map((branch) => ({
                    id: branch.id,
                  }))
                  .filter((item) => item.id !== branchId),
              });
          },

          onReactionAddClick: (
            branchId: Scalars["ID"],
            reactionType: AvatarAction["__typename"] | SystemAction["__typename"],
          ) => {
            const branches = action.branches.map((branch) => {
              let data: ChoiceUserInputBranchInput = {
                id: branch.id,
              };

              if (data.id === branchId) {
                switch (reactionType) {
                  case "AvatarAction":
                    data["hasAvatarReaction"] = true;
                    break;
                  case "SystemAction":
                    data["hasSystemReaction"] = true;
                    break;
                  default:
                    console.warn("Cannot add reaction of type " + reactionType);
                    break;
                }
              }

              return data;
            });

            onUpdate && onUpdate(action.id, action.__typename, {branches});
          },

          onReactionClick: (
            branchId: Scalars["ID"],
            reactionType: AvatarAction["__typename"] | SystemAction["__typename"],
          ) => {
            const branch = action.branches.find((branch) => branch.id === branchId);

            if (!branch) {
              console.warn("Cannot find ChoiceUserInput branch with id=" + branchId);
              return;
            }

            switch (reactionType) {
              case "AvatarAction":
                if (branch.avatarReaction) {
                  editActionModal(branch.avatarReaction);
                }
                break;
              case "SystemAction":
                if (branch.systemReaction) {
                  editActionModal(branch.systemReaction);
                }
                break;
              default:
                console.warn("Cannot edit reaction of type " + reactionType);
                break;
            }
          },

          onReactionDeleteClick: (
            branchId: Scalars["ID"],
            reactionType: AvatarAction["__typename"] | SystemAction["__typename"],
          ) => {
            const branches = action.branches.map((branch) => {
              let data: ChoiceUserInputBranchInput = {
                id: branch.id,
              };

              if (data.id === branchId) {
                switch (reactionType) {
                  case "AvatarAction":
                    data["hasAvatarReaction"] = false;
                    break;
                  case "SystemAction":
                    data["hasSystemReaction"] = false;
                    break;
                  default:
                    console.warn("Cannot add reaction of type " + reactionType);
                    break;
                }
              }

              return data;
            });

            onUpdate && onUpdate(action.id, action.__typename, {branches});
          },

          onDelete: () => onDelete && onDelete(action.id),
          onCopy: () => onCopy && onCopy(action.id),
        };

      default:
        throw new Error("[NotImplemented]");
    }
  }, [audioManager, challengeID, editActionModal, action, onDelete, onUpdate, onCopy, editQuestionModal]);

  const extraButtons = useMemo(() => {
    const buttons = {
      copyBtn: (
        <Button
          key="copy"
          size="s"
          onClick={handlers.onCopy}
          disabled={disabled}
          title="Copy action"
        >
          <CopyIcon fontSize={14} />
        </Button>
      ),
      deleteBtn: (
        <Button
          key="delete"
          size="s"
          onClick={handlers.onDelete}
          disabled={disabled}
          title="Delete action"
        >
          <DeleteIcon fontSize={14} />
        </Button>
      ),
    };

    return buttons;
  }, [handlers.onDelete, handlers.onCopy, disabled]);

  const mockClassName = ((getMockByActionID && getMockByActionID(action.id)) ?? {}).classes?.item;

  const rowProps = {
    className: mockClassName,
    extra: extraButtons,
  };

  const withAddRow = useCallback(
    (children: React.ReactNode) => (
      <>
        <ChallengeActionsAddRow.Collapsable disabled={disabled} onAdd={insertHandler} />
        {children}
      </>
    ),
    [disabled, insertHandler],
  );

  switch (action.__typename) {
    case "AvatarAction":
      return withAddRow(
        <Timeline.Row icon={<AvatarIcon avatar={challenge.avatar} />} {...rowProps}>
          <AvatarBubble action={action} onPlayClick={handlers.onPlayClick} onClick={handlers.onClick} />
        </Timeline.Row>,
      );
    case "SystemAction":
      return withAddRow(
        <Timeline.Row icon={<SystemIcon />} {...rowProps}>
          <SystemBubble action={action} onPlayClick={handlers.onPlayClick} onClick={handlers.onClick} />
        </Timeline.Row>,
      );
    case "UserInputAction":
      return withAddRow(
        <Timeline.Row icon={<UserIcon />} {...rowProps}>
          <UserInputBubble action={action} onClick={handlers.onClick} challengeMode={challenge.mode} />
        </Timeline.Row>,
      );

    case "ChoiceUserInputAction":
      return withAddRow(
        <Timeline.Row icon={<UserIcon />} {...rowProps}>
          <ChoiceUserInputBubble
            challengeId={challengeID}
            challengeMode={challenge.mode}
            avatar={challenge.avatar}
            action={action}
            scenarios={scenarios}
            allowScenarios={challenge.mode !== ChallengeMode.COMPETENCE_TEST}
            onMultipleChoiceToggle={disabled ? undefined : handlers.onMultipleChoiceToggle}
            onAddBranchClick={disabled ? undefined : handlers.onAddBranchClick}
            onBranchClick={handlers.onBranchClick}
            onDeleteBranchClick={disabled ? undefined : handlers.onDeleteBranchClick}
            onReactionAddClick={disabled ? undefined : handlers.onReactionAddClick}
            onReactionClick={handlers.onReactionClick}
            onReactionDeleteClick={disabled ? undefined : handlers.onReactionDeleteClick}
            onQuestionClick={handlers.onQuestionClick}
            showCustomScore={
              challenge.mode === ChallengeMode.CHOICES_WITH_CUSTOM_SCORES ||
              challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST
            }
            showMultipleChoice={
              challenge.mode === ChallengeMode.CHOICES_WITH_CUSTOM_SCORES ||
              challenge.mode === ChallengeMode.CUSTOM_PARAMETERS_TEST ||
              challenge.mode === ChallengeMode.CHOICE_BASED_STUDY
            }
          />
        </Timeline.Row>,
      );

    default:
      console.warn("Unsupported action type " + action.__typename);
      return <div />;
  }
}

ChallengeAction.Placeholder = function Placeholder() {
  const {t} = useTranslation();

  return (
    <Timeline.Row icon={<PlaceholderIcon />}>
      <Bubble disabled>
        <Bubble.Placeholder>{t("components.ChallengeActions.placeholder")}</Bubble.Placeholder>
      </Bubble>
    </Timeline.Row>
  );
};

ChallengeAction.fragments = {
  challenge: gql`
    fragment ChallengeActionChallenge on Challenge {
      id
      avatar {
        ...AvatarIcon
      }
    }

    ${AvatarIcon.fragments.root}
  `,

  root: gql`
    fragment ChallengeAction on Action {
      id
      __typename

      ... on AvatarAction {
        ...AvatarBubble
      }

      ... on SystemAction {
        ...SystemBubble
      }

      ... on UserInputAction {
        ...UserInputBubble
      }

      ... on ChoiceUserInputAction {
        ...ChoiceUserInputBubble
      }

      ...ActionEditForm
    }

    ${AvatarBubble.fragments.root}
    ${SystemBubble.fragments.root}
    ${UserInputBubble.fragments.root}
    ${ChoiceUserInputBubble.fragments.root}

    ${ActionEditForm.fragments.root}
  `,
};
